import React from 'react';
import PropTypes from 'prop-types';

import Browser from '@wetransfer/concorde-browser';
import classNames from 'classnames';

import I18n from '@module/i18n';

import Actions from '@app/store/action';
import Store from '@app/store';
import Analytics from '@module/analytics';
import { TransferAPI } from '@app/transfer-bundle/services/api/transfer';
import { FREE, PLUS_ACCOUNT } from '@constant/subscriptions';

import Scrollable from '@component/scrollable';
import { allowedSizeSelector } from '@app/store/selector/transfers';
import { getIsForwardedTransfer } from '@app/transfer-bundle/store/selectors';

import { isLoggedInFreeUser } from '@helper/user';

const BUBBLE_TIMEOUT = 7000;

function mapNotification(notification) {
  const state = Store.getState();
  const { transfer, user, channel } = state;

  const allowedSize = allowedSizeSelector(state);

  const TransferSizeAlertToolTip = {
    align: '.uploader__add-files',
    icon: 'error',
    text: 'transfer.bubble.errors.too_big',
    params: {
      upload_limit: I18n.size(allowedSize, { allowNull: true }),
      plus_limit: PLUS_ACCOUNT.TRANSFER_LIMIT,
    },
    onShow() {
      Analytics.trackSnowplowEvent({
        category: 'transfer',
        action: 'show_plus_upsell_20gb',
      });
    },
    upsell: {
      triggerOn() {
        return (!user.loggedIn || isLoggedInFreeUser(user)) && !channel.name;
      },
      icon: 'rocket',
      text: 'transfer.bubble.errors.too_big_text',
      title: 'transfer.bubble.errors.too_big_title',
      button: {
        text: 'transfer.bubble.buttons.plus',
        href: `/pro?trk=${
          isLoggedInFreeUser(user)
            ? 'WT202004_tooltip_20GB_fa_to_pro'
            : 'WT202006_tooltip_20GB'
        }`,
        action() {
          Analytics.trackSnowplowEvent({
            category: 'transfer',
            action: 'click_plus_upsell_20gb',
          });
        },
      },
    },
  };

  const mapping = {
    TooManyRequests: { icon: 'error', text: 'transfer.failed.title' },
    MissingFiles: { icon: 'error', text: 'transfer.bubble.errors.no_files' },
    TooManyFiles: {
      align: '.uploader__add-files',
      icon: 'error',
      text: 'transfer.bubble.errors.too_many_files',
      params: { count: user.rules.maximumNumberOfFiles },
    },
    EmptyFiles: { icon: 'error', text: 'transfer.bubble.errors.io_error' },
    DuplicateFilename: {
      icon: 'error',
      text: 'transfer.bubble.errors.duplicate_filename',
    },
    TransferTooBig: TransferSizeAlertToolTip,
    TooManyBytes: TransferSizeAlertToolTip,
    InvalidRecipient: {
      icon: 'error',
      text: 'transfer.bubble.errors.incorrect_recipient',
    },
    MissingRecipients: {
      icon: 'error',
      text: 'transfer.bubble.errors.no_recipient',
    },
    RequiresDefaultRecipient: {
      icon: 'error',
      button: { text: 'transfer.bubble.buttons.help', href: '/help' },
      text: 'transfer.bubble.errors.missing_default_recipient',
      params: { free_transfer_limit: FREE.TRANSFER_LIMIT },
    },
    TooManyRecipients: {
      icon: 'error',
      text: 'transfer.bubble.errors.too_many_recipient',
      params: { count: user.rules.maximumNumberOfRecipients || 0 },
      onShow() {
        Analytics.trackSnowplowEvent({
          category: 'transfer',
          action: 'show_tooltip',
          label: 'recipient_limit_reached',
        });
      },
      upsell: {
        triggerOn() {
          return !user.loggedIn || isLoggedInFreeUser(user);
        },
        icon: 'people',
        title: 'transfer.bubble.errors.too_many_recipient_title',
        text: 'transfer.bubble.errors.too_many_recipient_text',
        button: {
          text: 'transfer.bubble.buttons.plus',
          href: `/pro?trk=${
            isLoggedInFreeUser(user)
              ? 'WT202004_tooltip_recip_limit_fa_to_pro'
              : 'WT202006_tooltip_recip_limit'
          }`,
          action() {
            Analytics.trackSnowplowEvent({
              category: 'transfer',
              action: 'click_recipient_limit_reached',
            });
          },
        },
      },
    },
    MissingSender: { icon: 'error', text: 'transfer.bubble.errors.no_sender' },
    InvalidSender: {
      icon: 'error',
      text: 'transfer.bubble.errors.incorrect_sender',
    },
    MessageTooLarge: {
      icon: 'error',
      text: 'transfer.bubble.errors.long_message',
    },
    InvalidTransfer: {
      icon: 'error',
      text: getIsForwardedTransfer(state)
        ? 'transfer.bubble.errors.unavailable_forward'
        : 'transfer.bubble.errors.unavailable',
    },
    TransferNotDownloadable: {
      icon: 'error',
      text: 'transfer.bubble.errors.generic',
    },
    MissingSecret: { icon: 'error', text: 'transfer.bubble.errors.generic' },
    DownloadFailed: { icon: 'error', text: 'transfer.bubble.errors.generic' },
    InvalidPassword: {
      icon: 'error',
      text: 'transfer.bubble.errors.wrong_password',
    },
    TipFiles: {
      text: 'transfer.bubble.tips.free_limit',
      params: {
        size: I18n.size(allowedSize, { allowNull: true }),
      },
      onShow() {
        Analytics.trackSnowplowEvent({
          category: 'transfer',
          action: 'show_tooltip',
          label: 'add_files',
        });
      },
    },
    TipMessage: {
      text: 'transfer.bubble.tips.message',
      onShow() {
        Analytics.trackSnowplowEvent({
          category: 'transfer',
          action: 'show_tooltip',
          label: 'add_message',
        });
      },
    },
    TipTransferType: {
      text: 'transfer.bubble.tips.change_type',
      onShow() {
        Analytics.trackSnowplowEvent({
          category: 'transfer',
          action: 'show_tooltip',
          label: 'email_or_link',
        });
      },
    },
    TipRecipient: {
      text: 'transfer.bubble.tips.recipient_limit',
      params: { email_limit: user.rules.maximumNumberOfRecipients },
      onShow() {
        Analytics.trackSnowplowEvent({
          category: 'transfer',
          action: 'show_tooltip',
          label: 'add_recipient_email',
        });
      },
    },
    TipSenderRequireVerification: {
      text: 'transfer.bubble.tips.anonymous_sender.text',
      button: {
        text: 'transfer.email-verification-success.free_signup',
        href: '/sign-up?trk=WT202005_tooltip_verify_anonym_to_fa&tier=free',
      },
    },
    TipSenderRequireVerificationLockedRecipient: {
      text: 'transfer.bubble.tips.anonymous_sender.text_locked_recipient',
    },
    TipSenderLocked: {
      text: 'transfer.email-verification-success.verified_sender_tooltip',
      params: { href: '/account' },
      onShow() {
        Analytics.trackSnowplowEvent({
          category: 'transfer',
          action: 'show_tooltip',
          label: 'add_sender_email',
        });
      },
    },
    DiscoverVrlTooltip: {
      text: 'transfer.bubble.tips.vrl_text',
      timeout: 7500,
      title: 'transfer.bubble.tips.vrl_title',
    },
    RecaptchaFailed: {
      icon: 'error',
      title: 'transfer.bubble.errors.recaptcha.title',
      text: 'transfer.bubble.errors.recaptcha.text',
    },
    WrongVerificationCode: {
      align: '.transfer__textfield',
      title: 'transfer.bubble.errors.verify.title',
      text: 'transfer.bubble.errors.verify.text',
      button: {
        text: 'transfer.bubble.errors.verify.button',
        action() {
          TransferAPI.resendToken(transfer.id, {
            sender_email: transfer.sender,
          });
        },
      },
      upsell: {
        align: '.transfer__textfield',
        icon: 'error',
        title: 'transfer.bubble.errors.default_title',
        text: 'transfer.bubble.errors.verify.text',
        button: {
          text: 'transfer.bubble.errors.verify.button',
          action() {
            TransferAPI.resendToken(transfer.id, {
              sender_email: transfer.sender,
            });
          },
        },
      },
    },
    LockedRecipient: {
      align: '.uploader__locked-recipient',
      title: 'transfer.bubble.recipient_locked_title',
      text: 'transfer.bubble.recipient_locked_text',
    },
    FolderInsteadOfZip: {
      title: 'transfer.bubble.tips.zip_title',
      text: 'transfer.bubble.tips.zip_text',
    },
    PlusUpsell: {
      timeout: 5000,
      icon: 'rocket',
      text: 'transfer.upsell_one.bubble.text_experiment',
      title: 'transfer.upsell_one.bubble.title',
      upsell: true,
      onShow() {
        Analytics.trackSnowplowEvent({
          category: 'transfer',
          action: 'show_plus_upsell_pw',
        });
      },
      button: {
        text: 'transfer.bubble.buttons.plus',
        href: `/pro?trk=${
          isLoggedInFreeUser(user)
            ? 'WT202004_tooltip_options_fa_to_pro'
            : 'WT202006_tooltip_options'
        }`,
        action() {
          Analytics.trackSnowplowEvent({
            category: 'transfer',
            action: 'click_transfer_upgrade_tooltip',
          });
        },
      },
    },
    TransferEmailVerificationResendError: {
      text: 'transfer.email-verification-resend.error',
      align: '.email-verification__textfield',
      className: 'email-verification-notification',
    },
    TransferEmailVerificationResendThrottleError: {
      text: 'transfer.email-verification-resend.throttle-error',
      align: '.email-verification__textfield',
      className: 'email-verification-notification',
    },
    AccountEmailVerificationResendError: {
      text: 'transfer.email-verification-resend.account.error',
      align: '.email-verification__textfield',
      className: 'email-verification-notification',
    },
    AccountEmailVerificationResendThrottleError: {
      text: 'transfer.email-verification-resend.account.throttle-error',
      params: {
        throttle_time: I18n.t('datetime.distance_in_words.x_minutes', {
          count: 60,
        }),
      },
      align: '.email-verification__textfield',
      className: 'email-verification-notification',
    },
    AccountEmailVerificationLinkResendThrottleError: {
      text: 'transfer.email-verification-resend.throttle-error-link',
      params: {
        throttle_time: I18n.t('datetime.distance_in_words.x_minutes', {
          count: 60,
        }),
      },
      align: '.email-verification__textfield',
      className: 'account-email-verification-notification',
    },
    AccountEmailVerificationResendEmailExistsError: {
      text: 'transfer.email-verification-resend.account.error-email-exists',
      align: '.email-verification__textfield',
      className: 'email-verification-notification',
    },
    TransferEmailVerificationError: {
      text: 'transfer.email-verification.error-expiry-time',
      params: {
        expiry_time: I18n.t('datetime.distance_in_words.x_minutes', {
          count: 60,
        }),
      },
      align: '.email-verification__textfield',
      className: 'email-verification-notification',
    },
    TransferEmailVerificationThrottleError: {
      text: 'transfer.email-verification.throttle-error',
      align: '.email-verification__textfield',
      className: 'email-verification-notification',
    },
  };

  let result = mapping[notification.text];

  if (!result) {
    return notification;
  }

  // Replace tooltip with upsell content if the context triggers it.
  if (
    result.upsell &&
    typeof result.upsell.triggerOn === 'function' &&
    result.upsell.triggerOn()
  ) {
    // Inherit settings and button settings from original tooltip
    result = {
      ...result,
      ...result.upsell,
      button: {
        ...result.button,
        ...result.upsell.button,
      },
      upsell: true,
    };
  }

  // Add "Whoops!" as default title for error icons.
  if (result.icon === 'error') {
    result.title = result.title || 'transfer.bubble.errors.default_title';
  }

  // Translations!
  if (result.button && result.button.text) {
    result.button.text = I18n.t(result.button.text);
  }
  if (result.title) {
    result.title = I18n.t(result.title);
  }
  if (result.text) {
    result.text = I18n.t(result.text, result.params || {});
  }

  return {
    ...notification,
    ...result,
  };
}

export default class Bubble extends React.Component {
  static propTypes = {
    notification: PropTypes.object,
    parent: PropTypes.object,
    type: PropTypes.string,
  };

  constructor(props) {
    super(props);

    this.clearBubble = this.clearBubble.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.stopTimer = this.stopTimer.bind(this);
  }

  determineAlignment(notification) {
    //mapping the notification to verify if a preferred alignment is specified and present in the dom.
    const notificationObj = mapNotification(notification);
    if (
      notificationObj.align &&
      document.querySelector(notificationObj.align)
    ) {
      return notificationObj.align;
    }

    // No preferred alignment found so fallback on default based on the list below.
    switch (notification.text) {
      case 'MissingFiles':
      case 'TipFiles':
        return '.uploader__files';

      case 'DuplicateFilename':
      case 'TransferTooBig':
      case 'TooManyFiles':
      case 'TooManyBytes':
        return '.uploader__footer, .uploader__files';

      case 'TipSenderRequireVerification':
      case 'TipSenderRequireVerificationLockedRecipient':
      case 'MissingSender':
      case 'InvalidSender':
        return '.uploader__sender';

      case 'TipSenderLocked':
        return '.uploader__locked-sender';

      case 'InvalidRecipient':
        return '.uploader__recipientsContainer';

      case 'TipRecipient':
      case 'MissingRecipients':
      case 'TooManyRecipients':
      case 'RequiresDefaultRecipient':
        return '.uploader__recipient';

      case 'TipMessage':
      case 'MessageTooLarge':
        return '.uploader__message';

      case 'TipTransferType':
        return '.transfer__type';

      case 'InvalidPassword':
        return '.transfer__textfield';

      case 'RecaptchaFailed':
      case 'WrongVerificationCode':
        return '.transfer__footer';

      case 'PlusUpsell':
        return '.transfer__plus-features';

      case 'InvalidTransfer':
      case 'TransferNotDownloadable':
      case 'MissingSecret':
      case 'DownloadFailed':
        return false;
    }

    return false;
  }

  positionSelf(scrollToComponent = false) {
    const { notification, parent } = this.props;
    // This is where it gets tricky, because most bubbles will be shown
    // in relation to an element. We'll need to get the element and if
    // present the scrollview - but we don't have the React scrollframe.
    const scroll = parent.querySelector('.scrollable__content');

    // If this bubble DOM element cannot be found, we cannot do anything.
    // Same goes for details, that would mean there is no actual notification.
    if (!this.node || !notification) {
      return;
    }

    if (Browser.isMobile) {
      this.node.style.opacity = 1;
      return;
    }

    // Let's assume we will want to just center it next to the parent.
    let top = parent.offsetHeight / 2;

    // But wait, there is more!
    let align = this.determineAlignment(notification);

    // We'll want to align with something
    if (align) {
      // If align is a string, we'll need to querySelector it so
      // let's do that within the context of our parent.
      if (typeof align === 'string') {
        align = parent.querySelector(align);
        if (!align) {
          return;
        }
      }

      // Try to put the bubble at half position of the element.
      top =
        align.offsetHeight / 2 + align.offsetTop - this.node.offsetHeight / 2;
    }

    // If there is a scrollable component, we are in big trouble. Maybe.
    if (scroll) {
      const scrollTop = scroll.scrollTop;

      // Center the alignment element in scrollable and do the rest.
      if (scrollToComponent) {
        Scrollable.scrollTo(scroll, align, {
          align: 'center',
          animated: true,
        });
      }

      // Remove the scrollTop from the top position, otherwise
      // alignment is incorrect (it would be out of bounds)
      top -= scrollTop;
    }

    // If we are scrolling we want to clear the notification
    // once it goes out of bounds.
    if (!this.isLeaving) {
      this.node.style.opacity = 1;
    }

    if (scroll && !scrollToComponent) {
      if (top <= 0 || top >= scroll.offsetHeight) {
        this.node.style.opacity = 0;
      }
    }

    // Assign style to bubble DOM element
    const content = parent.querySelector('.transfer__contents');
    if (content) {
      this.node.style.top =
        Math.round(Math.min(content.offsetHeight, Math.max(0, top))) + 'px';
    }
  }

  componentDidUpdate(prevProps) {
    let scrollToComponent = false;
    const notification = mapNotification(this.props.notification);

    // If it's the same message we can most likely ignore it.
    if (
      !prevProps.notification ||
      prevProps.notification.date !== this.props.notification.date
    ) {
      // Just in case clear any timers that are set (and set a new one)
      this.stopTimer();
      this.startTimer();
      scrollToComponent = true;

      if (notification && typeof notification.onShow === 'function') {
        notification.onShow();
      }
    }

    // If noscroll is set, or transfer options are open, don't scroll.
    if (
      this.props.notification.noScroll ||
      (this.props.parent &&
        this.props.parent.querySelector('.transfer__options'))
    ) {
      scrollToComponent = false;
    }

    // Position the notification bubble
    this.positionSelf(scrollToComponent);

    // Do we need to attach our scroll listener?
    const scroll = this.props.parent.querySelector('.scrollable__content');
    if (scroll && !this.scrollBound) {
      // This is in component update because otherwise scroll
      // is not always available yet - but we also want to be
      // sure to bind the event only once - the flag is born.
      this.scrollBound = true;
      scroll.addEventListener('scroll', this.onScroll, false);
    }
  }

  componentDidMount() {
    this.componentDidUpdate({});
  }

  componentWillUnmount() {
    this.stopTimer();
    clearTimeout(this.componentWillLeaveTimeout);

    // Remove scroll listener, if present
    const scroll = this.props.parent.querySelector('.scrollable__content');
    if (scroll) {
      scroll.removeEventListener('scroll', this.onScroll, false);
    }
  }

  componentWillLeave(cb) {
    this.stopTimer();
    this.isLeaving = true;
    this.node.style.opacity = 0;
    this.componentWillLeaveTimeout = setTimeout(cb, 300);

    // Remove scroll listener, if present
    const scroll = this.props.parent.querySelector('.scrollable__content');
    if (scroll) {
      scroll.removeEventListener('scroll', this.onScroll, false);
    }
  }

  startTimer = () => {
    const timeout = this.props.notification.timeout;
    if (timeout !== 0) {
      this.timer = setTimeout(this.clearBubble, timeout || BUBBLE_TIMEOUT);
    }
  };

  clearBubble() {
    Store.dispatch(Actions.Notification.clearTransfer());
  }

  clickBubbleButton = () => {
    let { notification } = this.props;
    notification = mapNotification(notification);
    this.clearBubble();
    if (notification && notification.button && notification.button.action) {
      notification.button.action();
    }
  };

  stopTimer() {
    clearTimeout(this.timer);
  }

  onScroll() {
    if (this.props.notification.noScroll) {
      return;
    }
    this.positionSelf(false);
  }

  isButtonVisible(notification) {
    return (
      typeof notification.button.hidden === 'undefined' ||
      !notification.button.hidden
    );
  }

  makeButton(notification) {
    if (notification.button && this.isButtonVisible(notification)) {
      return (
        <a
          onClick={this.clickBubbleButton}
          className="popover__button"
          href={notification.button.href}
          target={notification.button.target || '_self'}
        >
          {notification.button.text}
        </a>
      );
    }

    return null;
  }

  notificationClass(notification) {
    if (typeof notification.text !== 'string') {
      return null;
    }

    return `transfer__bubble--${notification.text.toLowerCase()}`;
  }

  render() {
    let { notification } = this.props;
    if (!notification.text || typeof notification.text !== 'string') {
      return false;
    }

    const notificationClass = this.notificationClass(notification);

    notification = mapNotification(notification);

    const tooltipClassNames = classNames(
      'transfer__bubble',
      notificationClass,
      notification.className,
      {
        'transfer__bubble--upsell': notification.upsell === true,
        [`transfer__bubble--${notification.icon}`]: notification.icon,
      }
    );
    const text = <p dangerouslySetInnerHTML={{ __html: notification.text }} />;
    const image = notification.icon ? (
      <div className={`popover__image popover__image--${notification.icon}`} />
    ) : null;
    const title = notification.title ? (
      <strong className="transfer__bubble-title">{notification.title}</strong>
    ) : null;
    const button = this.makeButton(notification);
    return (
      <div
        ref={(node) => (this.node = node)}
        className={tooltipClassNames}
        onMouseEnter={this.stopTimer}
        onMouseLeave={this.startTimer}
      >
        {image}
        {title}
        {text}
        {button}
      </div>
    );
  }
}
